import { Group, Text, ActionIcon, Box, Anchor } from '@mantine/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconTrash, IconExternalLink } from '@tabler/icons-react';

interface SortableChildItemProps {
  id: number;
  index: number;
  title: string;
  displayId?: string;
  onRemove: (id: number) => void;
  onNavigate?: (id: number) => void;
}

export function SortableChildItem({
  id,
  index,
  title,
  displayId,
  onRemove,
  onNavigate,
}: SortableChildItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      py="xs"
      px="sm"
      bg={isDragging ? 'gray.0' : undefined}
      bd="1px solid var(--mantine-color-gray-2)"
      style={{
        ...style,
        borderRadius: 'var(--mantine-radius-sm)',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {/* Drag handle */}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            style={{ cursor: 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={16} />
          </ActionIcon>

          {/* Position number */}
          <Text size="sm" c="dimmed" fw={600} w={24} ta="center">
            #{index + 1}
          </Text>

          {/* Title */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {title}
            </Text>
            {displayId && (
              <Text size="xs" c="dimmed" ff="monospace" truncate>
                {displayId}
              </Text>
            )}
          </Box>
        </Group>

        <Group gap={4}>
          {onNavigate && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => onNavigate(id)}
            >
              <IconExternalLink size={14} />
            </ActionIcon>
          )}
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => onRemove(id)}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  );
}
